import "./App.css";
import LogIn from "./components/LogIn";
import LogOut from "./components/LogOut";
import SignUp from "./components/SignUp";
import ProfileSetup from "./components/ProfileSetup";
import ProfileSetupLayout from "./components/ProfileSetupLayout";
import DashboardLayout from "./components/DashboardLayout";
import LogInFlowLayout from "./components/LogInFlowLayout";
import Documents from "./components/Documents";
import DashboardMain from "./components/DashboardMain";
import DashboardGallery from "./components/DashboardGallery";
import ForgotPassword from "./components/ForgotPassword";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import theme from "./assets/theme/theme";
import { ThemeProvider } from "@mui/material";
import { AuthProvider } from "./contexts/AuthContext";
import { DocumentProvider } from "./contexts/DocumentContext";
import PreventRefreshDialogContext from "./contexts/PreventRefreshDialogContext";
import EmailVerification from "./components/EmailVerification";
import DCPage from "./components/DCPage";
import LegalSupport from "./components/LegalSupport/LegalSupport";
import TravelHistory from "./components/TravelHistory/TravelHistory";
import DocumentUploadProvider from "./contexts/DocumentUploadContext";
import AppBarLayout from "./components/AppBarLayout";
import DocumentSharePage from "./components/DocumentSharePage";
import { DocumentGalleryProvider } from "./contexts/DocumentGalleryContext";
import { DocumentShareModalProvider } from "./contexts/DocumentShareModalContext";
import { PublicDocumentSharePageProvider } from "./contexts/PublicDocumentSharePageContext";
import constants from "./constants";
import { UserProvider } from "./contexts/UserContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { DocumentDeleteModalProvider } from "./contexts/DocumentDeleteModalContext";
import { ScanningTipsModalProvider } from "./contexts/ScanningTipsModalContext";
import { PdfPreviewModalProvider } from "./contexts/PdfPreviewModalContext";
import FAQPage from "./components/FAQPage";
import Page404 from "./components/Page404";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { TravelHistoryProvider } from "./contexts/TravelHistoryContext";
import EnrollMFA from "./components/EnrollMFA";
import VerifyMfa from "./components/VerifyMfa";
import LogOutTimerProvider from "./contexts/LogOutTimer";
import Payment from "./components/Payment/Payment";
import { PaymentContextProvider } from "./contexts/PaymentContext";
import { WalkThroughModalProvider } from "./contexts/WalkThroughModalContext";
import { SnackbarProvider } from "notistack";
import ManualEditRequiredCombinedSnackbar from "./components/DashboardLayout/ManualEditRequiredCombinedSnackbar";
import DocumentExpiryCombinedSnackbar from "./components/DashboardLayout/DocumentExpiryCombinedSnackbar";
import { ErrorBoundary } from "react-error-boundary";
import Error from "./components/Error/Error";
import { TravelHistoryNew } from "./components/TravelHistoryNew/TravelHistoryNew";
import { TravelHistoryProviderNew } from "./components/TravelHistoryNew/TravelHistoryContextNew";
import NewNotificationArrivedSnackbar from "./components/DashboardLayout/NewNotificationArrivedSnackbar";
import * as Sentry from "@sentry/react";
import { getAppConfig } from "./utils/environment";
import TermsAndCondition from "./components/TermsAndCondition/TermsAndCondition";
import { Success } from "./components/Payment/Success";
import { CancelPayment } from "./components/Payment/Cancel";
import { GetExpertHelp } from "./components/GetExpertHelp/GetExpertHelp";
import VerifyMfaDelete from "./components/VerifyMfaDelete";
import AccountDeleteConfirmationPage from "./components/AccountDeleteConfirmationPage";
import O1EB1 from "./components/O1EB1";
import { O1EB1ContextProvider } from "./contexts/O1EB1DocumentsContext";
import O1EB1Documents from "./components/O1EB1/O1EB1Documents";
import CheatSheet from "./components/CheatSheet";
import { CustomCardProvider } from "./contexts/CreateCustomCardContext";
import UpgradePlan from "./components/Payment/UpgradePlan";
import { ExpertHelpPaymentSuccess } from "./components/GetExpertHelp/ExpertHelpPaymentSuccess";
import { CaseTrackingProvider } from "./contexts/CaseTrackingContext";
import CaseTracking from "./components/CaseTracking";
import CaseDetails from "./components/CaseTracking/CaseDetails";

declare module "notistack" {
  interface VariantOverrides {
    manualEditRequiredCombinedSnackbar: true;
    documentExpiryCombinedSnackbar: true;
    newNotificationArrivedSnackbar: true;
  }
}

if (process.env.REACT_APP_ENV === "prod") {
  Sentry.init({
    dsn: getAppConfig().sentry.dns,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [constants.docuCombLink, constants.apiLink],
      }),
      new Sentry.Replay(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

function App() {
  return (
    <ErrorBoundary fallback={<Error />}>
      <SnackbarProvider
        // preventDuplicate
        Components={{
          manualEditRequiredCombinedSnackbar:
            ManualEditRequiredCombinedSnackbar,
          documentExpiryCombinedSnackbar: DocumentExpiryCombinedSnackbar,
          newNotificationArrivedSnackbar: NewNotificationArrivedSnackbar,
        }}
      >
        <div className="App">
          <PreventRefreshDialogContext>
            <LogOutTimerProvider>
              <Router>
                <Switch>
                  <AuthProvider>
                    <PaymentContextProvider>
                      <UserProvider>
                        <DocumentProvider>
                          <CustomCardProvider>
                            <ThemeProvider theme={theme}>
                              <PdfPreviewModalProvider>
                                <ScanningTipsModalProvider>
                                  <WalkThroughModalProvider>
                                    <Switch>
                                      {/* LogIn Flow Routes */}
                                      <Route path="/signup" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.signup}
                                        >
                                          <SignUp />
                                        </DCPage>
                                      </Route>

                                      <Route path="/login" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.login}
                                        >
                                          <LogIn />
                                        </DCPage>
                                      </Route>

                                      <Route path="/faqs" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.faqs}
                                        >
                                          <FAQPage />
                                        </DCPage>
                                      </Route>

                                      <Route path="/privacy-policy" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.privacyPolicy}
                                        >
                                          <PrivacyPolicy />
                                        </DCPage>
                                      </Route>
                                      <Route path="/terms-and-condition" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.termsAndCondition}
                                        >
                                          <TermsAndCondition />
                                        </DCPage>
                                      </Route>

                                      <Route path="/email-verify" exact>
                                        <DCPage
                                          authRequired={true}
                                          emailVerifyRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.emailVerify}
                                        >
                                          <EmailVerification />
                                        </DCPage>
                                      </Route>

                                      <Route path="/forgot-password" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.forgotPassword}
                                        >
                                          <ForgotPassword />
                                        </DCPage>
                                      </Route>

                                      <Route path="/enroll-mfa" exact>
                                        <DCPage
                                          authRequired={true}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.enrollMFA}
                                        >
                                          <EnrollMFA />
                                        </DCPage>
                                      </Route>
                                      <Route path="/verify-mfa" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.verifyMfa}
                                        >
                                          <VerifyMfa />
                                        </DCPage>
                                      </Route>
                                      <Route path="/verify-mfa-delete" exact>
                                        <DCPage
                                          authRequired={true}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.verifyMfaDelete}
                                        >
                                          <VerifyMfaDelete />
                                        </DCPage>
                                      </Route>
                                      <Route path="/account-delete-confirmation" exact>
                                        <DCPage
                                          authRequired={false}
                                          layout={LogInFlowLayout}
                                          pageId={constants.pages.deleteConfirmationPage}
                                        >
                                          <AccountDeleteConfirmationPage />
                                        </DCPage>
                                      </Route>
                                      <Route path="/logout">
                                        <LogOut />
                                      </Route>

                                      <Route path="/shared/:shareReqId" exact>
                                        <PublicDocumentSharePageProvider>
                                          <DCPage
                                            authRequired={false}
                                            layout={AppBarLayout}
                                            pageId={constants.pages.sharedDocuments}
                                          >
                                            <DocumentSharePage />
                                          </DCPage>
                                        </PublicDocumentSharePageProvider>
                                      </Route>

                                      {/*--------- Document Upload Provider -----------*/}

                                      <DocumentUploadProvider>
                                        <Switch>
                                          <Route path="/legal-support" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.legalSupport}
                                            >
                                              <LegalSupport />
                                            </DCPage>
                                          </Route>
                                          <Route path="/travel-history" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.travelHistory}
                                            >
                                              <TravelHistoryProviderNew>
                                                <TravelHistoryNew />
                                              </TravelHistoryProviderNew>
                                            </DCPage>
                                          </Route>

                                          <Route path="/o1-eb1" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.o1eb1}
                                            >
                                              <O1EB1 />
                                            </DCPage>
                                          </Route>
                                          <Route path="/o1-eb1/:docType" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={
                                                constants.pages.o1eb1
                                              }
                                            >
                                              <DocumentGalleryProvider>
                                                <DocumentShareModalProvider>
                                                  <DocumentDeleteModalProvider>
                                                    <O1EB1ContextProvider>
                                                      <O1EB1Documents />
                                                    </O1EB1ContextProvider>
                                                  </DocumentDeleteModalProvider>
                                                </DocumentShareModalProvider>
                                              </DocumentGalleryProvider>
                                            </DCPage>
                                          </Route>

                                          <Route path="/travel-history-old" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.travelHistory}
                                            >
                                              <TravelHistoryProvider>
                                                <TravelHistory />
                                              </TravelHistoryProvider>
                                            </DCPage>
                                          </Route>

                                          <Route path="/subscription-and-billing" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.subscriptionAndBilling}
                                            >
                                              <Payment />
                                            </DCPage>
                                          </Route>

                                          <Route path="/case-tracking" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={
                                                constants.pages.caseTracking
                                              }
                                            >
                                              <CaseTrackingProvider>
                                                <CaseTracking />
                                              </CaseTrackingProvider>
                                            </DCPage>
                                          </Route>

                                          <Route path="/case-tracking/:receiptNumber" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={
                                                constants.pages.caseTracking
                                              }
                                            >
                                              <CaseTrackingProvider>
                                                <CaseDetails />
                                              </CaseTrackingProvider>
                                            </DCPage>
                                          </Route>

                                          <Route path="/subscription-and-billing/upgrade-plan" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={AppBarLayout}
                                              pageId={constants.pages.upgradePlan}
                                            >
                                              <UpgradePlan />
                                            </DCPage>
                                          </Route>

                                          <Route path="/subscription-and-billing/success" exact>
                                            <DCPage
                                              authRequired={false}
                                              layout={AppBarLayout}
                                              pageId={constants.pages.subscriptionAndBilling}
                                            >
                                              <Success />
                                            </DCPage>
                                          </Route>

                                          <Route path="/payment/success-expert-help" exact>
                                            <DCPage
                                              authRequired={false}
                                              layout={AppBarLayout}
                                              pageId={constants.pages.successExpertHelp}
                                            >
                                              <ExpertHelpPaymentSuccess />
                                            </DCPage>
                                          </Route>

                                          <Route path="/subscription-and-billing/cancel" exact>
                                            <DCPage
                                              authRequired={false}
                                              layout={AppBarLayout}
                                              pageId={constants.pages.subscriptionAndBilling}
                                            >
                                              <CancelPayment />
                                            </DCPage>
                                          </Route>

                                          {/* User Profile Setup Routes  */}
                                          <Route path="/profile/setup">
                                            <DCPage
                                              authRequired={true}
                                              layout={ProfileSetupLayout}
                                              pageId={
                                                constants.pages.userProfileSetup
                                              }
                                            >
                                              <UserProfileProvider>
                                                <ProfileSetup />
                                              </UserProfileProvider>
                                            </DCPage>
                                          </Route>

                                          {/* Dashboard Routes */}
                                          <Route path="/" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.dashboard}
                                            >
                                              <DocumentShareModalProvider>
                                                <DocumentDeleteModalProvider>
                                                  <DashboardMain />
                                                </DocumentDeleteModalProvider>
                                              </DocumentShareModalProvider>
                                            </DCPage>
                                          </Route>
                                          <Route path="/documents/:docType" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={
                                                constants.pages.viewUploadDocs
                                              }
                                            >
                                              <DocumentGalleryProvider>
                                                <DocumentShareModalProvider>
                                                  <DocumentDeleteModalProvider>
                                                    <Documents />
                                                  </DocumentDeleteModalProvider>
                                                </DocumentShareModalProvider>
                                              </DocumentGalleryProvider>
                                            </DCPage>
                                          </Route>

                                          <Route path="/documents" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={
                                                constants.pages.viewUploadDocs
                                              }
                                            >
                                              <DashboardGallery />
                                            </DCPage>
                                          </Route>
                                          <Route path="/cheat-sheet" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={AppBarLayout}
                                              pageId={
                                                constants.pages.cheatSheet
                                              }
                                            >
                                              <CheatSheet />
                                            </DCPage>
                                          </Route>

                                          <Route path="/get-expert-help" exact>
                                            <DCPage
                                              authRequired={true}
                                              layout={DashboardLayout}
                                              pageId={constants.pages.getExpertHelp}
                                            >
                                              <GetExpertHelp />
                                            </DCPage>
                                          </Route>

                                          <Route component={Page404} />
                                        </Switch>
                                      </DocumentUploadProvider>
                                    </Switch>
                                  </WalkThroughModalProvider>
                                </ScanningTipsModalProvider>
                              </PdfPreviewModalProvider>
                            </ThemeProvider>
                          </CustomCardProvider>
                        </DocumentProvider>
                      </UserProvider>
                    </PaymentContextProvider>
                  </AuthProvider>
                </Switch>
              </Router>
            </LogOutTimerProvider>
          </PreventRefreshDialogContext>
        </div>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}

export default App;
